import * as React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Alert, FormControl, Grid, InputLabel, MenuItem, OutlinedInput, Select, Typography } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { z as zod } from "zod";

import type { PriceBookItem, PriceBookPayload, PriceCode, PriceUnit } from "@/hooks/api/use-campaign-requests";
import { CustomDrawer } from "@/components/custom-drawer";
import { CustomFormHelperError } from "@/components/customFormHelper/custom-form-helper-error";

import { MARKETS, PRICE_CODE_LABELS, PRICE_CODE_UNIT_HINTS, PRICE_UNIT_OPTIONS } from "../constants";
import { labelOf } from "../humanise";
import { CARRIER_CODE, EDITABLE_CODES, isUnagreedRate } from "./rate-rows";
import { rateSchemaShape } from "./rate-schema";

const schema = zod.object({
	code: zod.string().min(1, "Pick a charge"),
	/** Empty string is a real answer — "every market" — and is sent as null. */
	market: zod.string(),
	...rateSchemaShape,
});

type FormValues = zod.infer<typeof schema>;

export interface RateDrawerTarget {
	/** Null sets the platform default; an id sets that account's override. */
	accountId: string | null;
	/** Whose rate this is, in words. The drawer never renders the id itself. */
	scopeLabel: string;
	code?: PriceCode;
	market?: string | null;
	/** Where the amount starts when an override is being created off an inherited default. */
	inheritedFrom?: PriceBookItem | null;
	/** True where code and market are decided by the row that opened this, not by the operator. */
	locked?: boolean;
}

export interface RateFormDrawerProps {
	open: boolean;
	target: RateDrawerTarget | null;
	isSaving: boolean;
	onClose: () => void;
	onSave: (payload: PriceBookPayload) => void;
}

function defaults(target: RateDrawerTarget | null): FormValues {
	const inherited = target?.inheritedFrom ?? null;

	return {
		code: target?.code ?? "",
		market: target?.market ?? "",
		amount: inherited?.amount ?? "",
		unit: (inherited?.unit ?? "each") as PriceUnit,
		currency: inherited?.currency || "SGD",
		// A seed's "nobody agreed this" note must never be copied onto a rate somebody just set by
		// hand — that would silently mark a rate unconfirmed that the operator believes they have
		// just answered.
		note: inherited && !isUnagreedRate(inherited.note) ? (inherited.note ?? "") : "",
	};
}

/**
 * Setting one rate: a platform default, or one account's override of it.
 *
 * Which of the two it is comes from the row that opened the drawer rather than from a control in
 * it. "Override HP Inc's call time" and "change what every account pays for call time" are not the
 * same decision with a different toggle, and a screen that lets one be mistaken for the other will
 * eventually have one mistaken for the other.
 */
export function RateFormDrawer({ open, target, isSaving, onClose, onSave }: RateFormDrawerProps): React.JSX.Element {
	const { control, handleSubmit, reset, watch } = useForm<FormValues>({
		resolver: zodResolver(schema),
		defaultValues: defaults(target),
	});
	// `FormControl` does not wire a label to its input the way `TextField` does, so without these
	// a screen reader announces each field as a bare "edit text".
	const fieldId = React.useId();

	React.useLayoutEffect(() => {
		reset(defaults(target));
	}, [target, reset, open]);

	const code = watch("code");
	const isCarrier = code === CARRIER_CODE;

	const submit = handleSubmit((values) => {
		const note = values.note.trim();

		onSave({
			code: values.code as PriceCode,
			accountId: target?.accountId ?? null,
			market: values.market === "" ? null : values.market,
			// A decimal string, exactly as typed. The DTO's regex rejects a number.
			amount: values.amount.trim(),
			unit: values.unit as PriceUnit,
			currency: values.currency,
			note: note.length > 0 ? note : null,
		});
	});

	return (
		<CustomDrawer
			open={open}
			onClose={onClose}
			title={target?.accountId ? "Set an account rate" : "Set a platform rate"}
			onSubmit={submit}
			isSubmitButtonDisabled={isSaving}
			submitButtonText="Save rate"
			width="520px"
		>
			<Grid container spacing={2}>
				<Grid size={12}>
					<Alert severity={target?.accountId ? "info" : "warning"}>
						<Typography variant="body2">
							{target?.accountId ? (
								<>
									This rate applies to <strong>{target.scopeLabel}</strong> only. It overrides the platform default
									wherever the two differ, and no other account is affected.
								</>
							) : (
								<>
									This is the <strong>platform default</strong>. Every account without its own override is quoted from
									it. It applies to future quotes only — a confirmed quote keeps the rates frozen onto its own lines.
								</>
							)}
						</Typography>
					</Alert>
				</Grid>

				<Grid size={12}>
					<Controller
						control={control}
						name="code"
						render={({ field, fieldState }) => (
							<FormControl error={Boolean(fieldState.error)} fullWidth required disabled={target?.locked}>
								<InputLabel id={`${fieldId}-code`}>Charge</InputLabel>
								<Select {...field} labelId={`${fieldId}-code`} label="Charge">
									{EDITABLE_CODES.map((option) => (
										<MenuItem key={option} value={option}>
											{labelOf(PRICE_CODE_LABELS, option)}
										</MenuItem>
									))}
								</Select>
								{fieldState.error ? (
									<CustomFormHelperError text={fieldState.error.message || ""} />
								) : code ? (
									<Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
										{PRICE_CODE_UNIT_HINTS[code as PriceCode]}
									</Typography>
								) : null}
							</FormControl>
						)}
					/>
				</Grid>

				<Grid size={12}>
					<Controller
						control={control}
						name="market"
						render={({ field, fieldState }) => (
							<FormControl error={Boolean(fieldState.error)} fullWidth disabled={target?.locked}>
								<InputLabel id={`${fieldId}-market`} shrink>
									Market
								</InputLabel>
								<Select {...field} labelId={`${fieldId}-market`} label="Market" displayEmpty>
									<MenuItem value="">All markets</MenuItem>
									{MARKETS.map((market) => (
										<MenuItem key={market.value} value={market.value}>
											{market.flag} {market.label}
										</MenuItem>
									))}
								</Select>
								{fieldState.error ? (
									<CustomFormHelperError text={fieldState.error.message || ""} />
								) : (
									<Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
										{isCarrier
											? "Carrier termination differs by destination, so it is priced per market."
											: "Leave as all markets unless this charge genuinely differs by destination."}
									</Typography>
								)}
							</FormControl>
						)}
					/>
				</Grid>

				<Grid size={{ xs: 12, sm: 8 }}>
					<Controller
						control={control}
						name="amount"
						render={({ field, fieldState }) => (
							<FormControl error={Boolean(fieldState.error)} fullWidth required>
								<InputLabel htmlFor={`${fieldId}-amount`}>Amount</InputLabel>
								<OutlinedInput
									{...field}
									id={`${fieldId}-amount`}
									label="Amount"
									placeholder="1200.0000"
									inputProps={{ inputMode: "decimal" }}
								/>
								{fieldState.error ? (
									<CustomFormHelperError text={fieldState.error.message || ""} />
								) : (
									<Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
										Up to 4 decimal places — carrier rates need them.
									</Typography>
								)}
							</FormControl>
						)}
					/>
				</Grid>

				<Grid size={{ xs: 12, sm: 4 }}>
					<Controller
						control={control}
						name="currency"
						render={({ field, fieldState }) => (
							<FormControl error={Boolean(fieldState.error)} fullWidth required>
								<InputLabel htmlFor={`${fieldId}-currency`}>Currency</InputLabel>
								<OutlinedInput {...field} id={`${fieldId}-currency`} label="Currency" placeholder="SGD" />
								{fieldState.error ? <CustomFormHelperError text={fieldState.error.message || ""} /> : null}
							</FormControl>
						)}
					/>
				</Grid>

				<Grid size={12}>
					<Controller
						control={control}
						name="unit"
						render={({ field, fieldState }) => (
							<FormControl error={Boolean(fieldState.error)} fullWidth required>
								<InputLabel id={`${fieldId}-unit`}>Unit</InputLabel>
								<Select {...field} labelId={`${fieldId}-unit`} label="Unit">
									{PRICE_UNIT_OPTIONS.map((option) => (
										<MenuItem key={option.value} value={option.value}>
											{option.label}
										</MenuItem>
									))}
								</Select>
								{fieldState.error ? <CustomFormHelperError text={fieldState.error.message || ""} /> : null}
							</FormControl>
						)}
					/>
				</Grid>

				<Grid size={12}>
					<Controller
						control={control}
						name="note"
						render={({ field, fieldState }) => (
							<FormControl error={Boolean(fieldState.error)} fullWidth>
								<InputLabel htmlFor={`${fieldId}-note`}>Note</InputLabel>
								<OutlinedInput {...field} id={`${fieldId}-note`} label="Note" placeholder="Never shown to a client" />
								{fieldState.error ? (
									<CustomFormHelperError text={fieldState.error.message || ""} />
								) : (
									<Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
										A note starting &ldquo;placeholder rate&rdquo; marks this as unconfirmed and stops it being quoted.
									</Typography>
								)}
							</FormControl>
						)}
					/>
				</Grid>

				<Grid size={12}>
					<Alert severity="info">
						<Typography variant="body2">
							Rates apply to <strong>future quotes only</strong>. Every quote copies the rates onto its own lines when
							it is priced, so nothing already confirmed moves.
						</Typography>
					</Alert>
				</Grid>
			</Grid>
		</CustomDrawer>
	);
}
